import { useParams, Link } from "react-router-dom";
import { Seo } from "../../components/Seo";
import { useGyms, useSchedule, type ScheduleSlot, type ClassFormat } from "../../api/hooks";
import { GymTabs } from "../../components/GymTabs";
import { LoadingSection } from "../../components/Spinner";

const DAYS = [
  { idx: 1, label: "Lun" },
  { idx: 2, label: "Mar" },
  { idx: 3, label: "Mer" },
  { idx: 4, label: "Jeu" },
  { idx: 5, label: "Ven" },
  { idx: 6, label: "Sam" },
];

type TimeBand = { key: string; startTime: string; endTime: string };

function bandKey(startTime: string, endTime: string) {
  return `${startTime}-${endTime}`;
}

function toMinutes(t: string): number {
  const parts = t.split(":").map(Number) as [number, number];
  return parts[0] * 60 + parts[1];
}

// true si la tranche `inner` est entièrement comprise dans `outer`.
function contains(outer: { startTime: string; endTime: string }, inner: { startTime: string; endTime: string }) {
  return toMinutes(inner.startTime) >= toMinutes(outer.startTime) && toMinutes(inner.endTime) <= toMinutes(outer.endTime);
}

// Tranches horaires (lignes de la grille), dérivées des cours réels. Une
// fermeture qui ne recouvre aucune tranche existante (ex. la pause déjeuner
// en semaine, sur laquelle aucun cours n'a jamais lieu) introduit sa propre
// ligne. En revanche une fermeture qui recouvre déjà des tranches existantes
// (ex. « fermé tout l'après-midi » le samedi) ne crée pas de ligne à elle :
// voir `isClosed`, qui ferme directement les lignes qu'elle recouvre — sinon
// elle apparaîtrait comme une ligne isolée, mal alignée avec le reste de la
// grille.
function timeBands(slots: ScheduleSlot[]): TimeBand[] {
  const bands: TimeBand[] = [];
  const seen = new Set<string>();
  for (const s of slots) {
    if (s.isClosure) continue;
    const key = bandKey(s.startTime, s.endTime);
    if (!seen.has(key)) {
      seen.add(key);
      bands.push({ key, startTime: s.startTime, endTime: s.endTime });
    }
  }
  for (const s of slots) {
    if (!s.isClosure) continue;
    const key = bandKey(s.startTime, s.endTime);
    if (seen.has(key)) continue;
    const range = { startTime: s.startTime, endTime: s.endTime };
    if (bands.some((b) => contains(range, b))) continue;
    seen.add(key);
    bands.push({ key, ...range });
  }
  return bands.sort(
    (a, b) => a.startTime.localeCompare(b.startTime) || a.endTime.localeCompare(b.endTime),
  );
}

// Index rapide « jour|tranche|zone » → créneau (hors fermetures), pour la grille.
function indexSlots(slots: ScheduleSlot[]): Map<string, ScheduleSlot> {
  const map = new Map<string, ScheduleSlot>();
  for (const s of slots) {
    if (s.isClosure) continue;
    map.set(`${s.dayOfWeek}|${s.startTime}-${s.endTime}|${s.zone ?? 1}`, s);
  }
  return map;
}

// Fermetures par jour, pour tester le recouvrement avec chaque ligne de la grille.
function closuresByDay(slots: ScheduleSlot[]): Map<number, TimeBand[]> {
  const map = new Map<number, TimeBand[]>();
  for (const s of slots) {
    if (!s.isClosure) continue;
    const list = map.get(s.dayOfWeek) ?? [];
    list.push({ key: bandKey(s.startTime, s.endTime), startTime: s.startTime, endTime: s.endTime });
    map.set(s.dayOfWeek, list);
  }
  return map;
}

function isClosed(day: number, band: TimeBand, closures: Map<number, TimeBand[]>): boolean {
  return (closures.get(day) ?? []).some((c) => contains(c, band));
}

export function Schedule() {
  const { gym = "brest" } = useParams();
  const { data: gyms } = useGyms();
  const { data: slots, isLoading } = useSchedule(gym);

  const currentGym = gyms?.find((g) => g.slug === gym);

  const bands = slots ? timeBands(slots) : [];
  const maxZone = slots && slots.length ? Math.max(1, ...slots.map((s) => s.zone ?? 1)) : 1;
  const zoneCols = Array.from({ length: maxZone }, (_, i) => i + 1);
  const slotIndex = slots ? indexSlots(slots) : new Map<string, ScheduleSlot>();
  const closures = slots ? closuresByDay(slots) : new Map<number, TimeBand[]>();

  // Formats réellement présents dans le planning de cette salle (hors fermetures,
  // dédupliqués), pour une légende alignée sur la grille et pilotée par l'admin.
  const legendFormats: ClassFormat[] = slots
    ? [
        ...new Map(
          slots.flatMap((s) => (s.format ? [[s.format.id, s.format] as const] : [])),
        ).values(),
      ].sort((a, b) => a.name.localeCompare(b.name))
    : [];

  return (
    <>
      <Seo
        title={`Planning ${currentGym?.name ?? ""}`}
        description={`Planning hebdomadaire des cours de CrossFit à ${currentGym?.name}. WOD, HIIT, Haltérophilie, Gymnastique, Kids.`}
        path={`/plannings/${gym}`}
      />

      <section className="bg-paper py-10 text-ink-900">
        <div className="container">
          <span className="eyebrow">Plannings</span>
          <h1 className="h-hero mt-2">{currentGym?.name ?? "Planning"}</h1>
          <GymTabs basePath="/plannings" className="mt-6" />
        </div>
      </section>

      <section className="section">
        <div className="container">
          {isLoading && <LoadingSection />}

          {!!slots?.length && (
            <div className="overflow-hidden rounded-lg border border-ink-100 bg-white shadow-sm">
              <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] table-fixed border-collapse">
                {/* Colonne horaire à largeur fixe ; les colonnes jour/zone se
                    partagent le reste à parts égales (SAM aussi large que les autres). */}
                {/* Colonne horaire figée à sa largeur de texte ; sur mobile chaque
                    colonne jour/zone est assez large pour le nom sur une seule ligne
                    (scroll horizontal), et redevient fluide/égale en desktop. */}
                <colgroup>
                  <col className="w-[66px] sm:w-[92px]" />
                  {DAYS.flatMap((d) =>
                    zoneCols.map((z) => (
                      <col key={`${d.idx}-${z}`} className="w-[104px] sm:w-auto" />
                    )),
                  )}
                </colgroup>
                <thead>
                  {/* Ligne 1 : jours (chacun couvrant ses zones) */}
                  <tr className="bg-brand-500 text-white">
                    <th
                      rowSpan={maxZone > 1 ? 2 : 1}
                      className="sticky left-0 z-20 border border-white/20 bg-brand-500 px-1.5 py-1.5 text-left font-display text-[10px] uppercase shadow-[2px_0_3px_-1px_rgba(0,0,0,0.15)] sm:px-2 sm:py-2 sm:text-xs"
                    >
                      Horaire
                    </th>
                    {DAYS.map((d, di) => (
                      <th
                        key={d.idx}
                        colSpan={maxZone}
                        className={`border border-white/20 px-1 py-1.5 text-center font-display text-[11px] uppercase sm:px-2 sm:py-2 sm:text-sm ${
                          di > 0 ? "border-l-2 border-l-white/40" : ""
                        }`}
                      >
                        {d.label}
                      </th>
                    ))}
                  </tr>
                  {/* Ligne 2 : zones (seulement si plusieurs zones utilisées) */}
                  {maxZone > 1 && (
                    <tr className="bg-brand-500 text-white/90">
                      {DAYS.map((d, di) =>
                        zoneCols.map((z, zi) => (
                          <th
                            key={`${d.idx}-${z}`}
                            className={`border border-white/20 px-0.5 py-0.5 text-center text-[8px] font-semibold uppercase tracking-wide sm:px-1 sm:py-1 sm:text-[10px] ${
                              zi === 0 && di > 0 ? "border-l-2 border-l-white/40" : ""
                            }`}
                          >
                            Zone {z}
                          </th>
                        )),
                      )}
                    </tr>
                  )}
                </thead>
                <tbody>
                  {bands.map((band) => (
                    <tr key={band.key}>
                      <th className="sticky left-0 z-10 whitespace-nowrap border border-ink-200 bg-ink-100 px-1 py-1.5 text-left text-[9px] font-bold tabular-nums text-ink-700 shadow-[2px_0_3px_-1px_rgba(0,0,0,0.15)] sm:px-2 sm:py-2 sm:text-xs">
                        {band.startTime}–{band.endTime}
                      </th>
                      {DAYS.map((d, di) => {
                        // Fermeture : une seule case « FERMÉ » couvrant les zones du jour.
                        if (isClosed(d.idx, band, closures)) {
                          return (
                            <td
                              key={d.idx}
                              colSpan={maxZone}
                              className={`border border-ink-200 bg-ink-100 ${
                                di > 0 ? "border-l-2 border-l-ink-300" : ""
                              }`}
                            />
                          );
                        }
                        return zoneCols.map((z, zi) => {
                          const s = slotIndex.get(`${d.idx}|${band.key}|${z}`);
                          return (
                            <td
                              key={`${d.idx}-${z}`}
                              className={`border border-ink-200 px-1 py-1.5 text-center align-middle sm:px-1.5 sm:py-2 ${
                                zi === 0 && di > 0 ? "border-l-2 border-l-ink-300" : ""
                              }`}
                            >
                              {s?.format && (
                                <span
                                  className="block whitespace-nowrap text-[10px] font-bold uppercase leading-tight sm:whitespace-normal sm:break-words sm:text-xs"
                                  style={{ color: s.format.color ?? "#DF2935" }}
                                  title={s.format.name}
                                >
                                  {s.format.name}
                                </span>
                              )}
                            </td>
                          );
                        });
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {slots?.length === 0 && (
            <p className="text-ink-500">
              Le planning n'est pas encore configuré. Un administrateur peut l'ajouter depuis
              l'espace admin.
            </p>
          )}

          {!!slots?.length && (
            <div className="mt-12 rounded-lg border border-ink-100 bg-white p-6 text-ink-900 shadow-sm sm:p-8">
              <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <span className="eyebrow">Envie d'essayer ?</span>
                  <h2 className="font-display text-2xl font-bold uppercase sm:text-3xl">
                    Réserve ta séance d'essai{currentGym ? ` à ${currentGym.name}` : ""}
                  </h2>
                  <p className="mt-2 text-sm text-ink-500">
                    Choisis directement un créneau dans le planning. C'est offert et sans
                    engagement.
                  </p>
                </div>
                <Link
                  to={`/contact?gym=${gym}&type=trial`}
                  className="btn-primary w-full sm:w-auto"
                >
                  Réserver ma séance d'essai
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      <FormatsLegend formats={legendFormats} />
    </>
  );
}

function FormatsLegend({ formats }: { formats: ClassFormat[] }) {
  if (formats.length === 0) return null;

  return (
    <section className="section bg-white">
      <div className="container">
        <span className="eyebrow">Les formats</span>
        <h2 className="h-section mt-2">
          {formats.length} {formats.length > 1 ? "formats" : "format"} d'entraînement
        </h2>
        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {formats.map((f) => (
            <div key={f.id} className="rounded-md bg-white p-4">
              <div className="font-display text-lg font-bold uppercase">{f.name}</div>
              {f.description && <div className="mt-1 text-sm text-ink-500">{f.description}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
